import { useRef, useState, useCallback, type ReactNode } from 'react';

interface SortableListProps<T> {
  items: T[];
  keyFn: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
  onReorder: (items: T[]) => void;
  className?: string;
}

export function SortableList<T>({ items, keyFn, renderItem, onReorder, className }: SortableListProps<T>) {
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const itemHeights = useRef<number[]>([]);

  const getItemIndex = useCallback((clientY: number): number => {
    const list = listRef.current;
    if (!list) return 0;
    const rect = list.getBoundingClientRect();
    const y = clientY - rect.top + list.scrollTop;
    let accumulated = 0;
    for (let i = 0; i < itemHeights.current.length; i++) {
      accumulated += itemHeights.current[i];
      if (y < accumulated) return i;
    }
    return items.length - 1;
  }, [items.length]);

  const captureHeights = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    itemHeights.current = Array.from(list.children).map(el => (el as HTMLElement).offsetHeight);
  }, []);

  const handleStart = useCallback((index: number, clientY: number) => {
    captureHeights();
    startY.current = clientY;
    setDragging(index);
    setOver(index);
  }, [captureHeights]);

  const handleMove = useCallback((clientY: number) => {
    if (dragging === null) return;
    const idx = getItemIndex(clientY);
    setOver(idx);
  }, [dragging, getItemIndex]);

  const handleEnd = useCallback(() => {
    if (dragging === null || over === null || dragging === over) {
      setDragging(null);
      setOver(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragging, 1);
    next.splice(over, 0, moved);
    onReorder(next);
    setDragging(null);
    setOver(null);
  }, [dragging, over, items, onReorder]);

  return (
    <div
      ref={listRef}
      className={className}
      onMouseMove={e => handleMove(e.clientY)}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchMove={e => { e.preventDefault(); handleMove(e.touches[0].clientY); }}
      onTouchEnd={handleEnd}
    >
      {items.map((item, i) => {
        const isDragging = dragging === i;
        const isOver = over === i && dragging !== null && dragging !== i;
        return (
          <div
            key={keyFn(item, i)}
            style={{
              opacity: isDragging ? 0.5 : 1,
              borderTop: isOver && over! < dragging! ? '2px solid var(--gold)' : undefined,
              borderBottom: isOver && over! > dragging! ? '2px solid var(--gold)' : undefined,
              transition: 'opacity 0.1s',
              touchAction: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span
                className="sortable-handle"
                onMouseDown={e => { e.preventDefault(); handleStart(i, e.clientY); }}
                onTouchStart={e => handleStart(i, e.touches[0].clientY)}
              >
                ⠿
              </span>
              <div style={{ flex: 1 }}>{renderItem(item, i)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
